/**
 * PanBrain Animations Engine
 * A reusable GSAP-based animation system for Shopify.
 * 
 * Features: 
 * - Smart Direction Reveal: Auto-detects left, right, or center position.
 * - Parallax Scrolling: Easy-to-use parallax effects.
 * - Staggered Animations: Sequential reveal for lists/grids.
 */

class PanBrainAnimations {
    constructor() {
        this.init();
    }

    init() {
        const runInit = () => {
            if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
                console.warn('PanBrain Animations: GSAP or ScrollTrigger not found.');
                return;
            }

            this.initReveal();
            this.initParallax();
            this.initStagger();

            // 初始刷新
            ScrollTrigger.refresh();
        };

        if (document.readyState === 'interactive' || document.readyState === 'complete') {
            runInit();
        } else {
            document.addEventListener('DOMContentLoaded', runInit);
        }

        // 页面资源（图片等）全部加载完后再刷新一次，确保复杂布局坐标无误
        window.addEventListener('load', () => ScrollTrigger.refresh());
    }

    /**
     * Smart Reveal Animation logic
     */
    initReveal() {
        const elements = document.querySelectorAll('.pan-reveal');

        elements.forEach(el => {
            const rect = el.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const elementCenter = rect.left + rect.width / 2;

            let direction = 'center';
            if (viewportWidth > 768) { // Only do directional logic on desktop
                if (elementCenter < viewportWidth * 0.35) {
                    direction = 'left';
                } else if (elementCenter > viewportWidth * 0.65) {
                    direction = 'right';
                }
            }

            // Read custom data attributes or use smart defaults
            const duration = parseFloat(el.dataset.panDuration) || 1.2;
            const delay = parseFloat(el.dataset.panDelay) || 0;
            const rotate = parseFloat(el.dataset.panRotate) || 0;
            const scale = parseFloat(el.dataset.panScale) || (direction === 'center' ? 0.95 : 1);

            let x = 0, y = 0;
            if (direction === 'left') x = -60;
            else if (direction === 'right') x = 60;
            else y = 40; // Center defaults to sliding up

            // Manual overrides if specified
            if (el.dataset.panDirection) {
                const manualDir = el.dataset.panDirection;
                x = 0; y = 0;
                if (manualDir === 'left') x = -60;
                if (manualDir === 'right') x = 60;
                if (manualDir === 'up') y = 60;
                if (manualDir === 'down') y = -60;
            }

            // Set will-change for performance
            el.style.willChange = 'transform, opacity';

            gsap.fromTo(el,
                {
                    opacity: 0,
                    x: x,
                    y: y,
                    rotation: rotate,
                    scale: scale,
                    filter: direction === 'center' ? 'blur(10px)' : 'blur(0px)'
                },
                {
                    opacity: 1,
                    x: 0,
                    y: 0,
                    rotation: 0,
                    scale: 1,
                    filter: 'blur(0px)',
                    duration: duration,
                    delay: delay,
                    ease: 'power3.out',
                    force3D: true,
                    scrollTrigger: {
                        trigger: el,
                        start: 'top+=50% bottom',
                        end: 'bottom-=50% top',
                        toggleActions: 'play reverse play reverse',
                        markers: false
                    }
                }
            );
        });
    }

    /**
     * Parallax Animation logic - Windowed approach
     */
    initParallax() {
        const parallaxItems = document.querySelectorAll('.pan-parallax');

        parallaxItems.forEach(el => {
            // Check if we should target an internal image for the "window" effect
            // User suggests: img inside overflow-hidden div moves
            const target = el.querySelector('.absolute.inset-0') || el.querySelector('img') || el.querySelector('.bg-cover') || el.querySelector('.pan-parallax-inner') || el;
            const speed = parseFloat(el.dataset.panSpeed) || 0.15; // Subtle default speed

            // If target is an image, we scale it up to provide headroom for movement
            if (target !== el) {
                el.style.overflow = 'hidden';
                // speed controls how far the image travels: speed=0.5 → ±25% yPercent
                const yAmount = speed * 50;
                const scaleAmount = 1 + Math.abs(yAmount) / 100 + 0.05; // ensure enough headroom
                gsap.set(target, {
                    scale: scaleAmount,
                    willChange: 'transform'
                });

                gsap.fromTo(target,
                    { yPercent: -yAmount },
                    {
                        yPercent: yAmount,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: el,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                );
            } else {
                // Fallback for simple element parallax
                gsap.to(el, {
                    y: (i, target) => -ScrollTrigger.maxScroll(window) * speed * 0.1,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: el,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true
                    }
                });
            }
        });

        // Section Parallax (Optional background segments)
        const bgParallax = document.querySelectorAll('.pan-parallax-bg');
        bgParallax.forEach(bg => {
            gsap.to(bg, {
                yPercent: 20,
                ease: 'none',
                scrollTrigger: {
                    trigger: bg,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true
                }
            });
        });
    }

    /**
     * Staggered Reveal logic
     */
    initStagger() {
        const containers = document.querySelectorAll('.pan-stagger');

        containers.forEach(container => {
            const children = container.children;
            if (children.length === 0) return;

            gsap.fromTo(children,
                {
                    opacity: 0,
                    y: 30
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: container,
                        start: 'top 80%',
                        toggleActions: 'play none none reverse'
                    }
                }
            );
        });
    }
}

// Instantiate the animation engine
new PanBrainAnimations();
